import { Group } from 'three';

/**
 * The contract every motif implements.
 *
 * A layer owns one idea from the text — the stair, the shells, the telescopes,
 * the wall of elders — and nothing else. It gets a context, builds its own
 * objects into `this.group`, and is ticked once a frame. Layers do not import
 * each other; they talk through `ctx.bus`.
 *
 * To add a motif: subclass this, give it a static `id`, and list it in
 * `core/Registry.js`. Nothing else in the project needs to change.
 */
export class Layer {
  /** @type {string} unique, kebab-case */
  static id = 'layer';

  /** Lower builds and updates first. The stair wants to exist before shells. */
  static order = 100;

  /**
   * @param {import('../core/App.js').Context} ctx
   */
  constructor(ctx) {
    this.ctx = ctx;
    this.group = new Group();
    this.group.name = new.target.id ?? this.constructor.id;
    this.enabled = true;
    this.built = false;
  }

  get id() {
    return this.constructor.id;
  }

  /** Create geometry and materials. May be async. Add things to `this.group`. */
  async build() {}

  /**
   * @param {number} dt seconds since the last frame, already clamped
   * @param {import('../core/App.js').FrameState} state
   */
  update(dt, state) {}

  /** @param {number} width @param {number} height */
  resize(width, height) {}

  /** Anything the inspector should show. Keep it cheap: it is read every frame. */
  stats() {
    return null;
  }

  /** Objects offered to the raycaster as candidates for desire. */
  gazeTargets() {
    return null;
  }

  /** Release GPU resources. Called on teardown and on hot-reload. */
  dispose() {
    this.group.traverse((o) => {
      o.geometry?.dispose?.();
      const m = o.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose?.());
      else m?.dispose?.();
    });
    this.group.removeFromParent();
    this.group.clear();
  }
}
